import { isSeconds, isWatchedRange, type WatchedRange } from "@carbon-os/server/contract";

/**
 * Video progress no answer has confirmed yet, kept in localStorage across page
 * loads so a closing tab loses nothing. One person's own: every key carries
 * the viewer key `/api/training` hands out (a hash of the Clerk user id, never
 * an email or a token), entries are only ever read for that person, and
 * signing out or switching account removes them all. Wrapped in try/catch
 * throughout: storage can be full, blocked or tampered with, and then it simply
 * keeps nothing.
 */
const clearListeners = new Set<() => void>();
export function onOutboxesCleared(listener: () => void): void {
  clearListeners.add(listener);
}

export const OUTBOX_PREFIX = "carbonos.video_outbox";
const MAX_MATERIALS = 20;
const MAX_RANGES = 500;
const MAX_BYTES = 64_000;

export interface OutboxEntry {
  ranges: WatchedRange[];
  positionSeconds: number;
  trackId: string;
  /** When it was written, used only to evict the oldest entries. */
  savedAt: number;
}

export type OutboxStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/** Where one person's outbox lives. */
export interface Outbox {
  storage: OutboxStorage;
  viewerKey: string;
}

export const outboxKey = (viewerKey: string) => `${OUTBOX_PREFIX}:${viewerKey}`;

export function browserStorage(): OutboxStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** This person's outbox, or null when there is no storage or no viewer to keep it for. */
export function outboxFor(viewerKey: string | null, storage: OutboxStorage | null = browserStorage()): Outbox | null {
  return storage && viewerKey ? { storage, viewerKey } : null;
}

/** Sorted, with overlapping and touching ranges joined. */
export function mergeRanges(ranges: WatchedRange[]): WatchedRange[] {
  const sorted = ranges.map(([start, end]): WatchedRange => [start, end]).sort((a, b) => a[0] - b[0]);
  const merged: WatchedRange[] = [];
  for (const [start, end] of sorted) {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged;
}

export function readOutbox(outbox: Outbox | null): Record<string, OutboxEntry> {
  if (!outbox) return {};
  let raw: unknown;
  try {
    raw = JSON.parse(outbox.storage.getItem(outboxKey(outbox.viewerKey)) ?? "{}");
  } catch {
    return {};
  }
  if (typeof raw !== "object" || raw === null) return {};
  const entries: Record<string, OutboxEntry> = {};
  for (const [materialId, value] of Object.entries(raw as Record<string, unknown>)) {
    const entry = value as Partial<OutboxEntry> | null;
    if (!entry || !Array.isArray(entry.ranges) || !isSeconds(entry.positionSeconds) || typeof entry.trackId !== "string") continue;
    const ranges = entry.ranges.filter(isWatchedRange);
    if (ranges.length === 0) continue;
    entries[materialId] = {
      ranges,
      positionSeconds: entry.positionSeconds,
      trackId: entry.trackId,
      savedAt: isSeconds(entry.savedAt) ? entry.savedAt : 0,
    };
  }
  return entries;
}

/** Replaces one material's entry, or removes it with null. Capped: oldest material and earliest ranges go first. */
export function writeOutbox(outbox: Outbox | null, materialId: string, entry: OutboxEntry | null): void {
  if (!outbox) return;
  try {
    const entries = readOutbox(outbox);
    if (entry && entry.ranges.length > 0) {
      entries[materialId] = { ...entry, ranges: mergeRanges(entry.ranges).slice(-MAX_RANGES) };
    } else delete entries[materialId];
    const ids = Object.keys(entries).sort((a, b) => (entries[b]?.savedAt ?? 0) - (entries[a]?.savedAt ?? 0));
    for (const id of ids.slice(MAX_MATERIALS)) delete entries[id];
    let text = JSON.stringify(entries);
    for (const id of ids.slice(0, MAX_MATERIALS).reverse()) {
      if (text.length <= MAX_BYTES) break;
      if (id !== materialId) delete entries[id];
      text = JSON.stringify(entries);
    }
    if (text.length > MAX_BYTES) return;
    const key = outboxKey(outbox.viewerKey);
    if (Object.keys(entries).length === 0) outbox.storage.removeItem(key);
    else outbox.storage.setItem(key, text);
  } catch {
    // Full or blocked: nothing is kept, and reporting carries on.
  }
}

/** Every person's outbox, gone: on sign-out and on switching account. */
export function clearOutboxes(storage: Storage | null = browserStorage() as Storage | null): void {
  for (const listener of clearListeners) listener();
  if (!storage) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith(OUTBOX_PREFIX)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
    // Blocked storage holds nothing to clear.
  }
}
